import { cs } from '../../../lib/currency';
import { Button } from '../../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  CreditCard, Banknote, Mail, Loader2, Eye, Edit, XCircle,
  ChevronLeft, ChevronRight, MessageSquare, Undo2, Receipt, ExternalLink, CheckCircle2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import PaymentSyncButton from '../PaymentSyncButton';
import SortableTable from '../../ui/sortableTable/SortableTable';
import { PlayerLinks } from '../MoneyLinks';
import { invoiceKey, isReminderEligible, getTypeBadge, getStatusBadge } from './utils.jsx';
import { resolveDisplayMethod } from './payment_method_helpers';
import { friendlyInvoiceNumber } from '../../../utils/invoiceNumber';

// iter278: defensive formatter — one bad timestamp in any of the
// hundreds of rows (legacy lane booking, manual invoice with a free-form
// `created_at`, etc.) would otherwise call format(Invalid Date, …) and
// throw RangeError, which blanks the entire <Table> body. Returns "-"
// for anything that can't be parsed.
//
// iter306gg: now takes an optional `tz` so dates render in the academy's
// business timezone (configured in Business Settings) rather than the
// admin's browser TZ. Both `pattern` and `tz` are optional for backwards
// compat with the existing call sites.
const FORMAT_TO_INTL = {
  'MMM d, yyyy': { month: 'short', day: 'numeric', year: 'numeric' },
  'MMM d': { month: 'short', day: 'numeric' },
};
const safeDate = (value, pattern = 'MMM d, yyyy', tz) => {
  if (!value) return '-';
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    const final = d && !Number.isNaN(d.getTime()) ? d : new Date(value);
    if (!final || Number.isNaN(final.getTime())) return '-';
    const intlOpts = FORMAT_TO_INTL[pattern];
    if (intlOpts && tz) {
      return new Intl.DateTimeFormat('en-US', { ...intlOpts, timeZone: tz }).format(final);
    }
    return format(final, pattern);
  } catch (_e) {
    return '-';
  }
};

const isSyncableProcessingInvoice = (invoice) => invoice?.status === 'processing' && ['lane_booking', 'tournament_registration', 'payment_transaction'].includes(invoice?.entity_type_for_sync) && Boolean(invoice?.id);
/**
 * The invoice list itself: bulk-action bar + sortable table + pagination.
 * Stateless re: data — all selection / pagination / sort state is owned
 * by the parent page.
 *
 * iter307ar — internally migrated to `<SortableTable>` for the
 * header/body. Sort + pagination remain externally controlled by the
 * parent page (server-fed `paginatedInvoices` + `filteredInvoices`).
 * Selection still rides through the `selectedIds` prop; SortableTable's
 * built-in checkbox column handles header + row toggles via
 * `selectable` / `selectedKeys` / `onSelectionChange`.
 */
export default function InvoiceTable({
  // data
  paginatedInvoices,
  filteredInvoices,
  loading,
  // sort
  onSort,
  sortColumn,
  sortDirection,
  // selection
  selectedIds,
  eligiblePageInvoices,
  eligibleFilteredInvoices,
  allPageSelected: _allPageSelected,  // computed inside SortableTable; kept for parity
  onTogglePage: _onTogglePage,
  onToggleRow,
  onSelectAllMatching,
  onClearSelection,
  // bulk actions
  bulkLaneEligible,
  onOpenBulkPaymentLink,
  onOpenBulkReminder,
  onOpenBulkCharge,
  // row actions
  onView,
  onEdit,
  onSendReminder,
  onCharge,
  onMarkPaid,
  onVoid,
  onUnvoid,
  onRefund,
  onSendReceipt,
  onMarkSettled,
  onSyncUpdated,
  // pagination
  currentPage,
  totalPages,
  itemsPerPage,
  onItemsPerPageChange,
  onPrevPage,
  onNextPage,
  // iter306gg: business timezone for date columns
  businessTz,
}) {
  // ---- Column defs for SortableTable ----
  const columns = [
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (invoice) => (
        <span className="text-slate-600">
          {safeDate(invoice.created_at, 'MMM d, yyyy', businessTz)}
        </span>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (invoice) => getTypeBadge(invoice.type, invoice.type_label),
    },
    {
      key: 'invoice_no',
      label: 'Invoice No.',
      sortable: false,
      render: (invoice) => {
        const num = friendlyInvoiceNumber(invoice);
        if (!num) return <span className="text-slate-400">—</span>;
        return (
          <button
            type="button"
            onClick={() => onView(invoice)}
            className="font-mono text-xs font-semibold text-emerald-400 hover:text-emerald-300 hover:underline"
            title="View invoice"
            data-testid={`invoice-no-${invoice.id}`}
          >
            {num}
          </button>
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      render: (invoice) => (
        <div>
          <p className="font-medium">{invoice.description}</p>
          {invoice.is_prorated && (
            <p className="text-xs text-purple-600 font-medium">Prorated for current month</p>
          )}
        </div>
      ),
    },
    {
      key: 'parent',
      label: 'Parent',
      sortable: true,
      render: (invoice) => {
        // iter307di — Parent name links to Parent Detail → Billing tab,
        // pre-filtered to this enrollment + MI when applicable. A small
        // external-link icon on the right opens the same page in a new
        // tab without leaving the invoice list.
        const billingParams = new URLSearchParams({ tab: 'billing' });
        if (invoice.type === 'enrollment') {
          if (invoice.entity_id) billingParams.set('enrollment', invoice.entity_id);
          if (invoice.monthly_invoice_id) billingParams.set('mi', invoice.monthly_invoice_id);
        }
        const billingHref = invoice.parent_id
          ? `/admin/parents/${invoice.parent_id}?${billingParams.toString()}`
          : null;
        return (
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              {billingHref ? (
                <Link
                  to={billingHref}
                  className="font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
                  data-testid={`invoice-row-parent-link-${invoice.id}`}
                >
                  {invoice.parent_name}
                </Link>
              ) : (
                <p className="font-medium">{invoice.parent_name}</p>
              )}
              <p className="text-xs text-slate-500 truncate">{invoice.parent_email}</p>
            </div>
            {billingHref && (
              <Link
                to={billingHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-emerald-600 mt-0.5"
                title="Open Parent Billing in new tab"
                data-testid={`invoice-row-billing-icon-${invoice.id}`}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        );
      },
    },
    {
      key: 'player',
      label: 'Player(s)',
      sortable: true,
      render: (invoice) => (
        <PlayerLinks
          players={(invoice.player_ids || []).map((id, i) => ({
            id,
            name: String(invoice.player_names || '').split(',')[i]?.trim(),
          })).filter((p) => p.name)}
          names={invoice.player_names}
        />
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      render: (invoice) => (
        <div className="font-semibold">
          {cs()}{Number(invoice.amount || 0).toFixed(2)}
          {invoice.is_prorated && invoice.monthly_fee && (
            <p className="text-xs text-slate-400">Full: {cs()}{Number(invoice.monthly_fee || 0).toFixed(2)}/mo</p>
          )}
          {invoice.original_amount && invoice.original_amount !== invoice.amount && !invoice.is_prorated && (
            <p className="text-xs text-slate-400 line-through">{cs()}{Number(invoice.original_amount || 0).toFixed(2)}</p>
          )}
          {(invoice.discount_amount || invoice.discount_reason) && (
            <p
              className="text-[10px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200"
              data-testid={`invoice-discount-chip-${invoice.id}`}
              title={
                invoice.discount_reason
                || (invoice.discount_amount
                  ? `−${cs()}${Number(invoice.discount_amount).toFixed(2)}`
                  : '')
              }
            >
              {invoice.discount_reason || 'Discount'}
              {invoice.discount_amount
                ? ` −${cs()}${Number(invoice.discount_amount).toFixed(2)}`
                : ''}
            </p>
          )}
          {/* iter309d — Refund chip is now per-MI (stamped on the
              monthly_invoice that was actually refunded by the
              services/refunds.py:issue_refund flow), so we no longer
              need the iter309c status guard against bleed. Display
              on PAID cycles (partial refund — invoice stays paid) and
              REFUNDED cycles (fully refunded — status flipped). */}
          {Number(invoice.refunded_amount || 0) > 0 && ['paid', 'refunded', 'partially_refunded'].includes(invoice.status) && (() => {
            // iter310 — Refund pill must subtract from the amount ACTUALLY
            // debited to the customer (amount_charged), not the invoice's
            // face amount. Prevents the "$99 invoice · $165 charged · $66
            // refunded" case from displaying a misleading "Net $33" pill;
            // net is truly $99 ($165 - $66). Falls back to `amount` for
            // legacy rows that predate the amount_charged stamp.
            const refunded = Number(invoice.refunded_amount || 0);
            const charged = Number(invoice.amount_charged || invoice.amount || 0);
            const net = Math.max(0, charged - refunded);
            // Full vs partial is judged against the BASE fee: a card refund
            // also returns the proportional surcharge, so a full refund lands
            // at or above the fee (charged includes that surcharge).
            const baseFee = Number(invoice.amount || 0) || charged;
            const fully = refunded + 0.005 >= baseFee;
            return (
              <p
                className="text-[10px] mt-0.5 inline-block max-w-[170px] px-1.5 py-0.5 rounded-md leading-snug bg-purple-50 text-purple-700 border border-purple-200"
                data-testid={`invoice-refund-chip-${invoice.id}`}
                title={
                  fully
                    ? `This invoice has been fully refunded (${cs()}${refunded.toFixed(2)} of ${cs()}${charged.toFixed(2)} charged)`
                    : `Charged ${cs()}${charged.toFixed(2)} · Refunded ${cs()}${refunded.toFixed(2)} · Net ${cs()}${net.toFixed(2)}`
                }
              >
                {fully ? 'Fully refunded' : `Refunded −${cs()}${refunded.toFixed(2)} · Net ${cs()}${net.toFixed(2)}`}
              </p>
            );
          })()}
          {/* iter317d — Credit Applied chip. Shows on any invoice where
              account credit was consumed at billing time (auto-billing
              path or an admin's manual "Mark Paid with Credit"). Green
              pill so admins can spot credit-funded cycles instantly. */}
          {Number(invoice.credit_applied || 0) > 0 && invoice.status !== 'partial' && (() => {
            const credit = Number(invoice.credit_applied || 0);
            const total = Number(invoice.amount || 0);
            const fullyByCredit = credit + 0.005 >= total;
            return (
              <p
                className="text-[10px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                data-testid={`invoice-credit-applied-chip-${invoice.id}`}
                title={
                  fullyByCredit
                    ? `Paid entirely by account credit (${cs()}${credit.toFixed(2)})`
                    : `${cs()}${credit.toFixed(2)} credit applied · ${cs()}${Math.max(0, total - credit).toFixed(2)} charged`
                }
              >
                {fullyByCredit ? `Credit −${cs()}${credit.toFixed(2)} (fully covered)` : `Credit −${cs()}${credit.toFixed(2)}`}
              </p>
            );
          })()}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (invoice) => {
        // iter318 wave 22b — Defensive: if the invoice is fully
        // refunded by math (refunded_amount ≥ amount_charged) show
        // REFUNDED regardless of the stored `status`. Historical rows
        // where a refund path only wrote refund fields (not status)
        // would otherwise render a misleading "PAID" badge next to
        // the "Fully refunded" chip.
        const refunded = Number(invoice.refunded_amount || 0);
        const charged = Number(invoice.amount_charged || invoice.amount || 0);
        const isEffectivelyRefunded =
          refunded > 0 && charged > 0 && refunded + 0.005 >= charged;
        const effectiveStatus =
          isEffectivelyRefunded && invoice.status !== 'void' && invoice.status !== 'refunded'
            ? 'refunded'
            : invoice.status;
        return (
          <div>
            <div className="flex items-center gap-1">
              {getStatusBadge(effectiveStatus)}
        {(isSyncableProcessingInvoice(invoice) || (['pending', 'overdue', 'processing', 'failed', 'partial'].includes(invoice.status)
                && ['lane_booking', 'tournament_registration', 'payment_transaction'].includes(invoice.entity_type_for_sync)
          && Boolean(invoice.stripe_session_id || invoice.stripe_payment_intent_id || invoice.stripe_txn_id)
        )) && (
                  <PaymentSyncButton
                    entityType={invoice.entity_type_for_sync}
                    entityId={invoice.entity_type_for_sync === 'payment_transaction' ? invoice.stripe_txn_id : invoice.id}
                    onUpdated={onSyncUpdated}
                    testId={`invoice-sync-${invoice.id}`}
                  />
                )}
            </div>
            {invoice.status === 'overdue' && invoice.payment_failure_reason && (
              <p
                className="text-xs text-red-700 mt-1 max-w-[180px]"
                title={invoice.payment_failure_reason}
                data-testid={`invoice-failure-reason-${invoice.id}`}
              >
                Payment failed: {invoice.payment_failure_reason}
              </p>
            )}
            {invoice.status === 'partial' && (
              <p
                className="text-xs text-amber-700 mt-1"
                data-testid={`invoice-partial-breakdown-${invoice.id}`}
              >
                Credit applied: {cs()}{Number(invoice.credit_applied || 0).toFixed(2)}
                {' · '}Due: <strong>{cs()}{Number(invoice.balance_due || 0).toFixed(2)}</strong>
            </p>
          )}
          {invoice.admin_notes && (
            <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              Has notes
            </p>
          )}
        </div>
        );
      },
    },
    {
      key: 'paid_at',
      label: 'Paid Date',
      sortable: true,
      render: (invoice) => (
        <span className="text-slate-600 whitespace-nowrap" data-testid={`invoice-paid-date-${invoice.id}`}>
          {invoice.paid_at
            ? safeDate(invoice.paid_at, 'MMM d, yyyy', businessTz)
            : <span className="text-slate-400">—</span>}
        </span>
      ),
    },
    {
      key: 'payment_method',
      label: 'Method',
      sortable: true,
      render: (invoice) => {
        const m = resolveDisplayMethod(invoice);
        if (!m) return <span className="text-xs text-slate-400">—</span>;
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${m.className}`}
            title={m.title}
            data-testid={`invoice-method-pill-${invoice.id}`}
          >
            {m.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (invoice) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onView(invoice)}
            title="View invoice details"
            data-testid={`view-invoice-${invoice.id}`}
          >
            <Eye className="h-4 w-4 text-slate-600" />
          </Button>
          {/* iter361 — Send Receipt is available for EVERY invoice status
              (paid, partially/fully refunded, overdue, pending, void) so an
              admin can always re-email the record. */}
          {onSendReceipt && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSendReceipt(invoice)}
              title="Email this invoice / receipt to the parent / customer"
              className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
              data-testid={`send-receipt-${invoice.id}`}
            >
              <Receipt className="h-4 w-4 mr-1" />
              Send Receipt
            </Button>
          )}
          {/* iter365 — Mark fully paid (close the loop) after an admin refund,
              so a partially-refunded / refunded invoice stops sending payment
              reminders. */}
          {onMarkSettled && !invoice.admin_settled &&
            (invoice.status === 'partially_refunded' || invoice.status === 'refunded') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMarkSettled(invoice)}
              title="Mark this invoice fully paid and stop payment reminders"
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              data-testid={`mark-settled-${invoice.id}`}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Mark Paid
            </Button>
          )}
          {invoice.admin_settled && (
            <span
              className="inline-flex items-center text-xs text-emerald-700"
              title="Marked fully paid by an admin — reminders suppressed"
              data-testid={`settled-badge-${invoice.id}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Settled
            </span>
          )}
          {invoice.status !== 'void' && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onEdit(invoice)}
                title="Edit price / Add notes"
                data-testid={`edit-invoice-${invoice.id}`}
              >
                <Edit className="h-4 w-4 text-blue-600" />
              </Button>
              {(invoice.status === 'pending' || invoice.status === 'overdue' || invoice.status === 'partial') && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onSendReminder(invoice)}
                    title="Send payment reminder email"
                    className="text-amber-700 border-amber-200 hover:bg-amber-50"
                    data-testid={`send-reminder-${invoice.id}`}
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onCharge(invoice)}
                    className="bg-blue-600 hover:bg-blue-700"
                    title="Charge saved card"
                    data-testid={`charge-invoice-${invoice.id}`}
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    Charge
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onMarkPaid(invoice)}
                    title="Record offline payment"
                    data-testid={`markpaid-invoice-${invoice.id}`}
                  >
                    <Banknote className="h-4 w-4 mr-1" />
                    Offline
                  </Button>
                </>
              )}
              {(invoice.status === 'paid' || invoice.status === 'partially_refunded') && onRefund && invoice.refund_status !== 'fully_refunded' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRefund(invoice)}
                  title="Issue a Stripe refund for this invoice"
                  className="text-amber-700 border-amber-200 hover:bg-amber-50"
                  data-testid={`refund-invoice-${invoice.id}`}
                >
                  <Undo2 className="h-4 w-4 mr-1" />
                  Refund
                </Button>
              )}
              {/* Void is available for every non-void status. When the
                  invoice carries a payment, VoidInvoiceDialog prompts the
                  admin to refund first. */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onVoid(invoice)}
                title="Void this invoice"
                data-testid={`void-invoice-${invoice.id}`}
              >
                <XCircle className="h-4 w-4 text-red-500" />
              </Button>
            </>
          )}
          {invoice.status === 'void' && onUnvoid && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUnvoid(invoice)}
              title="Restore this voided invoice back to pending"
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              data-testid={`unvoid-invoice-${invoice.id}`}
            >
              <Undo2 className="h-4 w-4 mr-1" />
              Unvoid
            </Button>
          )}
        </div>
      ),
    },
  ];

  // SortableTable's selection model uses row keys (strings). Translate
  // between our composite invoiceKey representation and the SortableTable API.
  const selectedKeyStrings = selectedIds.map(String);
  const handleSelectionChange = (nextKeys) => {
    // Compute diff against current — only the rows whose state changed
    // need toggle calls. (toggleRow accepts the full invoice object.)
    const prevSet = new Set(selectedKeyStrings);
    const nextSet = new Set(nextKeys);
    // Added
    for (const k of nextSet) {
      if (!prevSet.has(k)) {
        const inv = paginatedInvoices.find((i) => invoiceKey(i) === k);
        if (inv) onToggleRow(inv);
      }
    }
    // Removed
    for (const k of prevSet) {
      if (!nextSet.has(k)) {
        const inv = (paginatedInvoices.find((i) => invoiceKey(i) === k))
          || (filteredInvoices.find((i) => invoiceKey(i) === k));
        if (inv) onToggleRow(inv);
      }
    }
  };

  return (
    <>
      {(selectedIds.length > 0 || eligibleFilteredInvoices.length > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 bg-slate-50 rounded-lg px-4 py-3 mb-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium text-slate-700" data-testid="bulk-selection-count">
              {selectedIds.length === 0
                ? `${eligibleFilteredInvoices.length} eligible invoice${eligibleFilteredInvoices.length === 1 ? '' : 's'}`
                : `${selectedIds.length} selected`}
            </span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={onClearSelection}
                className="text-xs text-blue-600 hover:underline"
                data-testid="bulk-clear-selection"
              >
                Clear
              </button>
            )}
            {selectedIds.length === 0 && eligibleFilteredInvoices.length > eligiblePageInvoices.length && (
              <button
                type="button"
                onClick={onSelectAllMatching}
                className="text-xs text-blue-600 hover:underline"
                data-testid="bulk-select-all-matching"
              >
                Select all matching filter ({eligibleFilteredInvoices.length})
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onOpenBulkCharge && (
              <Button
                size="sm"
                onClick={onOpenBulkCharge}
                disabled={selectedIds.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="bulk-charge-btn"
                title="Charge the selected invoices to the card on file or record an offline payment"
              >
                <CreditCard className="h-4 w-4 mr-1" />
                Charge Selected
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={onOpenBulkPaymentLink}
              disabled={!bulkLaneEligible.eligible}
              title={bulkLaneEligible.reason || 'Send one Stripe payment link covering all selected lane bookings'}
              data-testid="bulk-payment-link-btn"
            >
              <CreditCard className="h-4 w-4 mr-1" />
              Combined Payment Link
            </Button>
            <Button
              size="sm"
              onClick={onOpenBulkReminder}
              disabled={selectedIds.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-send-reminder-btn"
              title={`Send "Invoice Reminder" template to selected parents`}
            >
              <Mail className="h-4 w-4 mr-1" />
              Send Payment Reminder
            </Button>
          </div>
        </div>
      )}

      <SortableTable
        data={paginatedInvoices}
        columns={columns}
        sortState={{ field: sortColumn, direction: sortDirection }}
        onSortChange={(next) => onSort(next.field)}
        pageSize={0}  // Pagination is managed externally by the parent
        selectable
        selectedKeys={selectedKeyStrings}
        onSelectionChange={handleSelectionChange}
        isRowSelectable={isReminderEligible}
        getRowKey={(invoice) => invoiceKey(invoice)}
        loading={loading}
        emptyMessage="No invoices found"
        testidPrefix="invoice"
      />

      {filteredInvoices.length > 0 && (
        <div className="flex items-center justify-between px-4 py-4 border-t">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredInvoices.length)} of {filteredInvoices.length} invoices
            </span>
            <Select value={itemsPerPage.toString()} onValueChange={(v) => onItemsPerPageChange(Number(v))}>
              <SelectTrigger className="w-[100px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="25">25 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
                <SelectItem value="100">100 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onPrevPage} disabled={currentPage === 1}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <span className="text-sm px-2">Page {currentPage} of {totalPages || 1}</span>
            <Button variant="outline" size="sm" onClick={onNextPage} disabled={currentPage >= totalPages}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
